import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { EmberCanvas } from "@/components/site/EmberCanvas";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  {
    num: "01",
    title: "Zuhören & Analysieren",
    text: "Wir schauen uns Ihre Abläufe vor Ort oder per Video an — vom Telefon über die Terminvergabe bis zur Ablage. Erst verstehen, dann vorschlagen.",
  },
  {
    num: "02",
    title: "Bauen & Einführen",
    text: "Wir setzen die Lösung um und integrieren sie in Ihre bestehenden Systeme — Schritt für Schritt, ohne Ihren Betrieb zu unterbrechen.",
  },
  {
    num: "03",
    title: "Begleiten & Verbessern",
    text: "Nach dem Start bleiben wir an Ihrer Seite: Wir messen, was funktioniert, schulen Ihr Team und entwickeln die Lösung kontinuierlich weiter.",
  },
];

export const Approach = () => {
  const sectionRef = useRef(null);
  const strokeRef = useRef(null);
  const strokeWrapRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { backgroundColor: "#CFCAC2" },
        {
          backgroundColor: "#1E1B18",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
            end: "top 25%",
            scrub: true,
          },
        }
      );

      if (!reduced) {
        gsap.fromTo(
          strokeRef.current,
          { x: 0 },
          {
            x: () => Math.min(0, -(strokeRef.current.scrollWidth - window.innerWidth)),
            ease: "none",
            scrollTrigger: {
              trigger: strokeWrapRef.current,
              start: "top bottom",
              end: "top 15%",
              scrub: true,
              invalidateOnRefresh: true,
            },
          }
        );
      }

      gsap.utils.toArray(".approach-line").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: reduced ? 0 : 28 },
          {
            opacity: 1,
            y: 0,
            duration: 0.85,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 88%" },
          }
        );
      });

      // Quote: scrub-linked blur reveal
      gsap.fromTo(
        ".approach-quote",
        { opacity: 0, y: 36, filter: "blur(10px)" },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          ease: "none",
          scrollTrigger: {
            trigger: ".approach-quote",
            start: "top 95%",
            end: "top 55%",
            scrub: true,
          },
        }
      );

      // Gold line draws itself across as you scroll
      gsap.fromTo(
        ".approach-gold-line",
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: "none",
          scrollTrigger: {
            trigger: ".approach-steps",
            start: "top 92%",
            end: "bottom 55%",
            scrub: true,
          },
        }
      );

      // Step columns drift at different speeds (scrub parallax)
      if (!reduced) {
        gsap.utils.toArray(".approach-step").forEach((el, i) => {
          gsap.fromTo(
            el,
            { y: 30 + i * 26 },
            {
              y: -(16 + i * 22),
              ease: "none",
              scrollTrigger: {
                trigger: ".approach-steps",
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            }
          );
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="ansatz"
      data-section-idx="2"
      data-dark="true"
      className="relative overflow-hidden py-32 lg:py-44"
      style={{ backgroundColor: "#1E1B18" }}
      data-testid="approach-section"
    >
      <EmberCanvas />

      <div
        ref={strokeWrapRef}
        className="pointer-events-none relative mb-20 select-none lg:mb-28"
        aria-hidden="true"
      >
        <div
          ref={strokeRef}
          className="stroke-text font-display inline-block text-[5rem] leading-none sm:text-[8rem] lg:text-[11rem] will-change-transform"
        >
          KI für echte Unternehmen
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <p className="approach-line eyebrow mb-6 text-[#F5F1E8]/60">003 / 006 — Unser Ansatz</p>
        <h2 className="approach-line font-display max-w-3xl text-4xl text-[#F5F1E8] sm:text-5xl lg:text-6xl">
          Keine Experimente. Ergebnisse.
        </h2>
        <p className="approach-quote font-serif-accent mt-8 max-w-2xl text-2xl leading-snug text-[#E8C887] sm:text-3xl">
          „Technologie, die sich anfühlt wie ein guter Mitarbeiter —
          ruhig, zuverlässig, immer da."
        </p>

        <div className="approach-gold-line mt-20 h-px origin-left bg-gradient-to-r from-[#E8C887] via-[#D4A95C] to-transparent opacity-60 lg:mt-28" />
        <div className="approach-steps mt-14 grid gap-14 md:grid-cols-3 lg:gap-12">
          {STEPS.map((step, i) => (
            <div key={step.num} className="approach-step" data-testid={`approach-step-${i}`}>
              <span className="approach-line approach-step-num block">{step.num}</span>
              <h3 className="approach-line font-display mt-6 text-xl text-[#F5F1E8] sm:text-2xl">
                {step.title}
              </h3>
              <p className="approach-line mt-5 text-sm leading-relaxed text-[#F5F1E8]/60 sm:text-base">
                {step.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
